import React from 'react';
import './PrivacyPolicy.css';

const PrivacyPolicy = () => {
  return (
    <div className="policy-page">
      <div className="policy-header">
        <div className="container">
          <h1>Privacy Policy</h1>
          {/* <p>Last updated: April 27, 2026</p> */}
        </div>
      </div>

      <div className="container policy-content my-5">
        <p className="intro">
          This Privacy Policy explains how Thrive Holdings Limited and its subsidiaries ("Thrive Group", "we", "us", or "our") collects, uses, stores, shares, and protects personal information when you visit our website, use our services, or interact with our communications. Thrive Holdings Limited operates through two main subsidiaries: Thrive Insurance Brokers Limited and Thrive Travels & Tours Limited.
        </p>

        <section>
          <h2>Scope of This Policy</h2>
          <p>
            This policy applies to all personal data collected through our website, mobile applications, customer service channels, events, insurance quote requests, travel bookings, and any other interactions where Thrive Holdings Limited, Thrive Insurance Brokers Limited, or Thrive Travels & Tours Limited collects personal information.
          </p>
        </section>

        <section>
          <h2>Information We Collect</h2>
          <p>
            We collect several types of information in order to provide and improve our insurance brokerage and travel services. This includes:
          </p>
          <ul>
            <li><strong>Identity Information:</strong> name, title, company name, and account username for insurance and travel accounts.</li>
            <li><strong>Contact Information:</strong> email address, phone number, postal address, and other contact details you provide for insurance quotes and travel bookings.</li>
            <li><strong>Insurance Information:</strong> coverage needs, risk assessment data, policy details, claims history, and insurance preferences.</li>
            <li><strong>Travel Information:</strong> destination preferences, travel dates, booking history, travel companions, dietary preferences, and special travel requirements.</li>
            <li><strong>Transactional Information:</strong> information related to insurance inquiries, insurance policies, travel bookings, service requests, event registrations, and communications with Thrive.</li>
            <li><strong>Technical Information:</strong> IP address, device identifiers, browser type, operating system, referral source, pages visited, and other usage data.</li>
            <li><strong>Marketing Information:</strong> preferences for receiving communications about insurance products and travel packages, interests, and responses to our marketing content.</li>
          </ul>
        </section>

        <section>
          <h2>How We Collect Information</h2>
          <p>
            We collect information in the following ways:
          </p>
          <ul>
            <li><strong>Directly from you:</strong> when you fill out forms, contact us, subscribe to newsletters, or interact with support.</li>
            <li><strong>Automatically:</strong> when you use our website and services, through cookies, analytics tools, and other tracking technologies.</li>
            <li><strong>From third parties:</strong> such as service providers, marketing partners, or publicly available sources when permitted by law.</li>
          </ul>
        </section>

        <section>
          <h2>Why We Use Your Information</h2>
          <p>
            We use your personal information for purposes that include:
          </p>
          <ul>
            <li>Providing, operating, and maintaining our website and services through our insurance brokerage and travel divisions.</li>
            <li>Processing insurance quote requests and providing personalized insurance recommendations.</li>
            <li>Processing travel bookings and providing travel itinerary and destination information.</li>
            <li>Responding to inquiries, fulfilling requests, and communicating with you about insurance and travel services.</li>
            <li>Managing insurance policies, claims processing, and policy renewals.</li>
            <li>Improving our user experience, website functionality, and content offerings for both divisions.</li>
            <li>Analyzing website traffic and usage patterns to optimize performance and service delivery.</li>
            <li>Delivering personalized content, promotions, and relevant offers for insurance products and travel packages.</li>
            <li>Protecting against fraud, abuse, and security threats in insurance transactions and travel bookings.</li>
            <li>Complying with insurance regulations and travel industry standards.</li>
            <li>Complying with legal obligations, resolving disputes, and enforcing our policies.</li>
          </ul>
        </section>

        <section>
          <h2>Legal Basis for Processing</h2>
          <p>
            Where applicable, we process your personal data based on several lawful grounds, including your consent, our legitimate interests (such as improving our services and protecting our systems), and our obligations to comply with applicable laws.
          </p>
        </section>

        <section>
          <h2>How We Share Your Information</h2>
          <p>
            We do not sell your personal information. We may share your information with:
          </p>
          <ul>
            <li><strong>Service providers:</strong> companies that help us operate our website, process insurance quotes, handle travel bookings, process payments, host data, or provide support.</li>
            <li><strong>Insurance partners:</strong> insurance underwriters, claims processors, and insurance regulatory bodies for insurance-related services.</li>
            <li><strong>Travel partners:</strong> airlines, hotels, tour operators, and travel service providers for travel bookings and destination services.</li>
            <li><strong>Business partners:</strong> partners who help us deliver marketing, event management, and analytics services for both insurance and travel divisions.</li>
            <li><strong>Subsidiaries:</strong> Thrive Insurance Brokers Limited and Thrive Travels & Tours Limited, which are part of the Thrive Holdings Limited group.</li>
            <li><strong>Legal and regulatory authorities:</strong> when required to comply with insurance regulations, travel industry standards, laws, protect our rights, or respond to lawful requests.</li>
            <li><strong>Business transfers:</strong> in the event of a merger, acquisition, or sale of assets, where personal information may be transferred as part of the business transaction.</li>
          </ul>
        </section>

        <section>
          <h2>International Data Transfers</h2>
          <p>
            We may transfer your personal data to service providers or partners located in countries outside your jurisdiction. When we do so, we take steps to ensure appropriate safeguards are in place to protect your information in accordance with applicable data protection laws.
          </p>
        </section>

        <section>
          <h2>Data Retention</h2>
          <p>
            We retain your personal information only as long as necessary to fulfill the purposes for which it was collected, comply with legal obligations, resolve disputes, and enforce agreements. Retention periods may vary depending on the type of information and the purpose for processing.
          </p>
        </section>

        <section>
          <h2>Data Security</h2>
          <p>
            We use reasonable and appropriate technical, administrative, and organizational measures to protect your personal data from unauthorized access, disclosure, alteration, or destruction. While we strive to protect your information, no method of transmission or storage is completely secure.
          </p>
        </section>

        <section>
          <h2>Your Rights</h2>
          <p>
            Depending on your jurisdiction, you may have certain rights regarding your personal information, including the right to:
          </p>
          <ul>
            <li>Access the data we hold about you.</li>
            <li>Correct or update inaccurate information.</li>
            <li>Delete your personal data where permitted.</li>
            <li>Restrict or object to certain processing activities.</li>
            <li>Withdraw consent where processing is based on consent.</li>
          </ul>
          <p>
            To exercise these rights, please contact us using the details provided below.
          </p>
        </section>

        <section>
          <h2>Children's Privacy</h2>
          <p>
            Our website is not intended for children under the age of 13. We do not knowingly collect personal information from children without parental consent. If we learn that we have collected personal information from a child without appropriate consent, we will take steps to delete it.
          </p>
        </section>

        <section>
          <h2>Cookies and Tracking</h2>
          <p>
            We use cookies and similar technologies to collect information about your interactions with our website. For more details, please refer to our <a href="/cookie-policy">Cookie Policy</a>.
          </p>
        </section>

        <section>
          <h2>Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy periodically to reflect changes in our practices, technology, or legal requirements. When we make material changes, we will post the updated policy on this page and revise the "Last updated" date.
          </p>
        </section>

        <section>
          <h2>Contact Us</h2>
          <p>
            If you have questions about this Privacy Policy, wish to exercise your rights, or have concerns regarding your insurance or travel information, please contact us through our <a href="/contact">Contact Us</a> page. You can also reach out to our insurance or travel divisions directly with service-specific inquiries.
          </p>
        </section>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
